class VaillantVideoControls {
  constructor(host, options = {}) {
    this.host = host;
    this.root = host.querySelector('[data-vaillant-video-controls]');
    this.getActiveVideo = options.getActiveVideo || (() => host.querySelector('video'));
    this.getVideos = options.getVideos || (() => [...host.querySelectorAll('video')]);
    this.onBeforePlay = options.onBeforePlay || (() => {});
    this.userPaused = false;

    if (!this.root) return;

    this.playButton = this.root.querySelector('[data-vaillant-video-play]');
    this.muteButton = this.root.querySelector('[data-vaillant-video-mute]');
    this.fullscreenButton = this.root.querySelector('[data-vaillant-video-fullscreen]');
    this.progressRoot = this.root.querySelector('[data-vaillant-video-progress]');
    this.progressTrack = this.root.querySelector('.vaillant-video-controls__progress-track');
    this.progressFill = this.root.querySelector('[data-vaillant-video-progress-fill]');

    this.handleFullscreenChange = () => this.updateFullscreenState();
    this.progressFrame = null;

    this.bindEvents();
    this.refresh();

    this.getVideos().forEach((video) => {
      if (!video.paused) this.startProgressLoop();
    });
  }

  bindEvents() {
    this.playButton?.addEventListener('click', (event) => {
      event.stopPropagation();
      this.togglePlayback();
    });

    this.muteButton?.addEventListener('click', (event) => {
      event.stopPropagation();
      this.toggleMute();
    });

    this.fullscreenButton?.addEventListener('click', (event) => {
      event.stopPropagation();
      this.toggleFullscreen();
    });

    this.progressRoot?.addEventListener('click', (event) => {
      event.stopPropagation();
      this.seek(event);
    });

    this.getVideos().forEach((video) => {
      video.addEventListener('timeupdate', () => this.updateProgress(video));
      video.addEventListener('loadedmetadata', () => this.updateProgress(video));
      video.addEventListener('durationchange', () => this.updateProgress(video));
      video.addEventListener('playing', () => {
        this.refresh();
        this.startProgressLoop();
      });
      video.addEventListener('pause', () => {
        this.refresh();
        this.stopProgressLoop();
        this.updateProgress(video);
      });
      video.addEventListener('ended', () => {
        this.stopProgressLoop();
        this.updateProgress(video);
      });
      video.addEventListener('volumechange', () => this.updateMuteState());
      video.addEventListener('webkitbeginfullscreen', this.handleFullscreenChange);
      video.addEventListener('webkitendfullscreen', this.handleFullscreenChange);
    });

    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
  }

  destroy() {
    this.stopProgressLoop();
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
  }

  startProgressLoop() {
    if (this.progressFrame) return;

    const tick = () => {
      const video = this.getActiveVideo();
      if (!video || video.paused) {
        this.stopProgressLoop();
        return;
      }

      this.updateProgress(video);
      this.progressFrame = requestAnimationFrame(tick);
    };

    this.progressFrame = requestAnimationFrame(tick);
  }

  stopProgressLoop() {
    if (!this.progressFrame) return;
    cancelAnimationFrame(this.progressFrame);
    this.progressFrame = null;
  }

  refresh() {
    this.updatePlaybackState();
    this.updateMuteState();
    this.updateProgress();
    this.updateFullscreenState();
  }

  togglePlayback() {
    const video = this.getActiveVideo();
    if (!video) return;

    if (video.paused) {
      this.userPaused = false;
      this.onBeforePlay(video);
      VaillantVideoMedia.playWhenReady(video);
    } else {
      this.userPaused = true;
      video.pause();
    }
  }

  toggleMute() {
    const video = this.getActiveVideo();
    if (!video) return;

    const shouldMute = !video.muted;
    this.getVideos().forEach((item) => {
      item.muted = shouldMute;
    });
    this.updateMuteState();
  }

  toggleFullscreen() {
    const video = this.getActiveVideo();
    if (!video) return;

    this.onBeforePlay(video);
    this.userPaused = false;
    video.play()?.catch(() => {});

    if (document.fullscreenElement === video) {
      document.exitFullscreen?.();
      return;
    }

    video.controls = true;

    if (video.requestFullscreen) {
      video.requestFullscreen()?.catch(() => {
        video.controls = false;
      });
    } else if (video.webkitEnterFullscreen) {
      video.webkitEnterFullscreen();
    } else {
      video.controls = false;
    }
  }

  seek(event) {
    const video = this.getActiveVideo();
    if (!video || !this.progressTrack || !Number.isFinite(video.duration) || video.duration <= 0) return;

    const rect = this.progressTrack.getBoundingClientRect();
    const ratio = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);
    video.currentTime = ratio * video.duration;
    this.updateProgress();
  }

  updateProgress(sourceVideo) {
    const video = sourceVideo || this.getActiveVideo();
    if (!video || !this.progressFill) return;

    const duration = video.duration;
    const ratio =
      Number.isFinite(duration) && duration > 0 ? Math.min(Math.max(video.currentTime / duration, 0), 1) : 0;

    this.progressFill.style.transform = `scaleX(${ratio})`;
  }

  updatePlaybackState() {
    const video = this.getActiveVideo();
    if (!video) return;

    const isPaused = video.paused;
    this.host.classList.toggle('is-video-paused', isPaused);
    this.host.classList.toggle('is-video-playing', !isPaused);
    this.host.classList.toggle('is-paused', isPaused);
    this.host.classList.toggle('is-playing', !isPaused);
    this.playButton?.setAttribute('aria-label', isPaused ? 'Play video' : 'Pause video');
  }

  updateMuteState() {
    const video = this.getActiveVideo();
    if (!video) return;

    this.host.classList.toggle('is-video-muted', video.muted);
    this.muteButton?.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
    this.muteButton?.setAttribute('aria-pressed', video.muted ? 'true' : 'false');
  }

  updateFullscreenState() {
    const video = this.getActiveVideo();
    const isFullscreen =
      document.fullscreenElement === video || Boolean(video?.webkitDisplayingFullscreen);

    if (video && !isFullscreen) video.controls = false;
    this.fullscreenButton?.setAttribute('aria-pressed', isFullscreen ? 'true' : 'false');
    this.fullscreenButton?.setAttribute('aria-label', isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen');
  }
}

window.VaillantVideoControls = VaillantVideoControls;

window.VaillantVideoMedia = {
  hydrate(video) {
    if (!video || video.dataset.loaded === 'true') return;

    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });
    video.dataset.loaded = 'true';
    video.load();
  },

  playWhenReady(video, { userPaused = false, onPlaying } = {}) {
    if (!video || userPaused) return;

    const attemptPlay = () => {
      const playPromise = video.play();
      playPromise?.then(() => onPlaying?.()).catch(() => {});
    };

    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      attemptPlay();
      return;
    }

    video.addEventListener('canplay', attemptPlay, { once: true });
  },

  loadAndPlay(video, options = {}) {
    this.hydrate(video);
    this.playWhenReady(video, options);
  },
};
